import { useEffect, useState } from 'react';
import api from '../lib/api';

export default function OutlookPage() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/integrations/outlook/status')
      .then(r => setStatus(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await api.post('/integrations/outlook/sync');
      setSyncResult({ success: true, message: `Synced ${r.data.synced} emails successfully.` });
    } catch (err) {
      setSyncResult({ success: false, message: err.response?.data?.error || 'Sync failed.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Outlook? This will stop email syncing.')) return;
    await api.delete('/integrations/outlook/disconnect');
    setStatus({ connected: false, connection: null });
  };

  if (loading) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>;

  return (
    <>
      <div className="integration-card">
        <div className="integration-header">
          <div className="integration-icon outlook">✉</div>
          <div>
            <div style={{ fontSize:14, fontWeight:500 }}>Outlook add-in</div>
            <div style={{ fontSize:12, color:'var(--text-secondary)' }}>
              {status?.connected
                ? <><span className="status-dot connected"></span>Connected · {status.connection.email}</>
                : <><span className="status-dot error"></span>Not connected</>
              }
            </div>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            {status?.connected
              ? <>
                  <button className="btn btn-primary" onClick={handleSync} disabled={syncing}>
                    {syncing ? 'Syncing…' : 'Sync now'}
                  </button>
                  <button className="btn" onClick={handleDisconnect}>Disconnect</button>
                </>
              : <a className="btn btn-primary" href="http://localhost:4000/api/integrations/outlook/connect">
                  Connect Outlook
                </a>
            }
          </div>
        </div>

        {syncResult && (
          <div style={{
            padding:'10px 12px', borderRadius:'var(--radius)', fontSize:13, marginTop:8,
            background: syncResult.success ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: syncResult.success ? 'var(--success)' : 'var(--danger)'
          }}>
            {syncResult.message}
          </div>
        )}
      </div>

      {status?.connected && (
        <div className="card">
          <div className="section-title">Sync settings</div>
          <table className="data-table">
            <tbody>
              <tr>
                <td style={{ color:'var(--text-secondary)' }}>Auto-log inbound emails</td>
                <td><span className={`badge ${status.connection.sync_emails ? 'badge-green' : 'badge-gray'}`}>
                  {status.connection.sync_emails ? 'On' : 'Off'}
                </span></td>
              </tr>
              <tr>
                <td style={{ color:'var(--text-secondary)' }}>Sync calendar meetings</td>
                <td><span className={`badge ${status.connection.sync_calendar ? 'badge-green' : 'badge-gray'}`}>
                  {status.connection.sync_calendar ? 'On' : 'Off'}
                </span></td>
              </tr>
              <tr>
                <td style={{ color:'var(--text-secondary)' }}>Auto-create contacts from email</td>
                <td><span className={`badge ${status.connection.auto_create_contacts ? 'badge-green' : 'badge-amber'}`}>
                  {status.connection.auto_create_contacts ? 'On' : 'Manual approval'}
                </span></td>
              </tr>
              <tr>
                <td style={{ color:'var(--text-secondary)' }}>Last synced</td>
                <td style={{ fontSize:12 }}>
                  {status.connection.last_synced_at
                    ? new Date(status.connection.last_synced_at).toLocaleString()
                    : 'Never'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!status?.connected && (
        <div className="card">
          <div className="section-title">How to connect</div>
          <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.7 }}>
            <p style={{ marginBottom:8 }}>Connecting Outlook requires a free Microsoft Azure app registration. You will need:</p>
            <ol style={{ paddingLeft:20 }}>
              <li>A Microsoft 365 business account</li>
              <li>Access to <strong>portal.azure.com</strong> to register a free app</li>
              <li>Your Azure Client ID and Secret added to the backend .env file</li>
            </ol>
            <p style={{ marginTop:8 }}>Once configured, click <strong>Connect Outlook</strong> above to authorize the connection.</p>
          </div>
        </div>
      )}
    </>
  );
}